import React from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import DashboardService from "../../Services/DashboardService";
import { EmptyIcon } from "../../Common/FullPageSpinner";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const d = payload[0]?.payload || {};
    return (
      <div style={{
        background: "#fff",
        border: "1px solid rgba(24,198,168,0.18)",
        borderRadius: 10,
        padding: "10px 14px",
        boxShadow: "0 4px 20px rgba(15,27,60,0.12)",
        fontFamily: "Inter, sans-serif",
        minWidth: 140,
      }}>
        <div style={{ fontSize: 11, color: "#8fa3b8", fontWeight: 600, marginBottom: 6 }}>
          Practice {d.id ?? label}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#18C6A8" }} />
          <span style={{ fontSize: 14, fontWeight: 800, color: "#18C6A8" }}>{d.value ?? 0}</span>
          <span style={{ fontSize: 11, color: "#8fa3b8" }}>total</span>
        </div>
        <div style={{ fontSize: 11, color: "#8fa3b8", lineHeight: 1.8 }}>
          Active: <strong style={{ color: "#1a2a4a" }}>{d.active ?? 0}</strong>
          {" · "}Inactive: <strong style={{ color: "#1a2a4a" }}>{d.inActive ?? 0}</strong>
          {" · "}Deleted: <strong style={{ color: "#1a2a4a" }}>{d.deleted ?? 0}</strong>
        </div>
      </div>
    );
  }
  return null;
};

const PracticeUserCard = ({ fromDate, toDate }: { fromDate: string; toDate: string }) => {
  const [chartData, setChartData] = React.useState<any[]>([]);
  const [loader, setLoader] = React.useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = React.useState<string>("total");
  const [rawData, setRawData] = React.useState<any[]>([]);

  React.useEffect(() => {
    setLoader(true);
    DashboardService.getPracticeUserDetails(fromDate, toDate)
      .then((response) => {
        if (response?.result && response?.result?.length > 0) {
          setRawData(response.result);
          applyFilter(selectedFilter, response.result);
        } else {
          setRawData([]);
          setChartData([]);
        }
      })
      .catch((error) => console.error("Error fetching chart data:", error))
      .finally(() => setLoader(false));
  }, [fromDate, toDate]);

  const applyFilter = (filter: string, data: any[]) => {
    const sorted = [...data].sort((a, b) => a?.id - b?.id);
    const mapped = sorted.map((ch: any) => {
      const base = {
        name: `${ch?.id}`,
        id: ch?.id ?? "",
        value: ch?.total ?? 0,
        active: ch?.active ?? 0,
        inActive: ch?.inActive ?? 0,
        deleted: ch?.deleted ?? 0,
      };
      switch (filter) {
        case "active":   return { ...base, value: ch?.active ?? 0 };
        case "inActive": return { ...base, value: ch?.inActive ?? 0 };
        case "deleted":  return { ...base, value: ch?.deleted ?? 0 };
        default:         return base;
      }
    });
    setChartData(mapped);
  };

  React.useEffect(() => {
    if (rawData?.length > 0) applyFilter(selectedFilter, rawData);
  }, [selectedFilter]);

  return (
    <Spin spinning={loader} indicator={<LoadingOutlined style={{ fontSize: 30, color: "#18C6A8" }} spin />}>
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData} margin={{ top: 8, right: 36, left: 0, bottom: 8 }}>
            <defs>
              <linearGradient id="userAreaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#5EE6C8" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#18C6A8" stopOpacity={0.01} />
              </linearGradient>
              <linearGradient id="userLineGradient" x1="1" y1="0" x2="0" y2="0">
                <stop offset="0%" stopColor="#5EE6C8" />
                <stop offset="100%" stopColor="#18C6A8" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 4" stroke="#f0f4ff" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: "#8fa3b8", fontSize: 11, fontFamily: "Inter, sans-serif" }}
              tickLine={false}
              axisLine={{ stroke: "#e8eef8" }}
            />
            <YAxis
              tick={{ fill: "#8fa3b8", fontSize: 11, fontFamily: "Inter, sans-serif" }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(24,198,168,0.08)", strokeWidth: 2 }} />
            <Area
              type="monotone"
              dataKey="value"
              stroke="url(#userLineGradient)"
              strokeWidth={3}
              fill="url(#userAreaGradient)"
              dot={false}
              activeDot={{ r: 5, fill: "#18C6A8", stroke: "#fff", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 60, gap: 10 }}>
          <EmptyIcon />
        </div>
      )}
    </Spin>
  );
};

export default PracticeUserCard;